import React, { useState, useEffect } from "react";
import { Plus, Pill, Calendar, IndianRupee, Edit2, Trash2, CalendarCheck, CreditCard, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useClientSupplements, useDeleteClientSupplement } from "@/hooks/useInventory";
import { ClientSupplementWithDetails } from "@/types/inventory";
import { getPaymentStatusColor } from "@/types/inventory";
import { AssignSupplementModal } from "@/components/supplements/AssignSupplementModal";

type SupplementsTabProps = {
  clientId: string;
};

export function SupplementsTab({ clientId }: SupplementsTabProps) {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingSupplement, setEditingSupplement] = useState<ClientSupplementWithDetails | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const { data, isLoading } = useClientSupplements(clientId);
  const deleteSupplement = useDeleteClientSupplement(clientId);

  const supplements = data?.supplements || [];

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleDelete = async (supplementId: string, productName: string) => {
    if (!confirm(`Are you sure you want to remove ${productName}?`)) {
      return;
    }

    try {
      await deleteSupplement.mutateAsync(supplementId);
      setToast({ message: "Supplement removed successfully", type: 'success' });
    } catch (error: any) {
      setToast({ message: error.message || "Failed to remove supplement", type: 'error' });
    }
  };

  const handleEdit = (supplement: ClientSupplementWithDetails) => {
    setEditingSupplement(supplement);
    setShowAssignModal(true);
  };

  const handleCloseModal = () => {
    setShowAssignModal(false);
    setEditingSupplement(null);
  };

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-[#5B8E87] rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      {supplements.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
            <Pill className="w-8 h-8 text-[#5B8E87]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No supplements assigned
          </h3>
          <p className="text-gray-500 text-center mb-6 max-w-sm">
            Assign supplements to track inventory and manage client orders
          </p>
          <button
            onClick={() => setShowAssignModal(true)}
            className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg hover:bg-[#4A7069] transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Assign Supplement
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {supplements.map((supplement) => (
            <SupplementCard
              key={supplement.id}
              supplement={supplement}
              onDelete={handleDelete}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}

      {/* Floating Action Button */}
      {supplements.length > 0 && (
        <button
          onClick={() => setShowAssignModal(true)}
          className="fixed bottom-20 right-6 bg-[#5B8E87] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#4A7069] transition-colors z-30"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Assign/Edit Supplement Modal */}
      {showAssignModal && (
        <AssignSupplementModal
          clientId={clientId}
          editingSupplement={editingSupplement}
          onClose={handleCloseModal}
          onSuccess={(message) => {
            setToast({ message, type: 'success' });
            handleCloseModal();
          }}
          onError={(message) => setToast({ message, type: 'error' })}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
          <div
            className={`shadow-lg rounded-lg px-4 py-3 text-sm font-medium ${
              toast.type === 'error'
                ? 'bg-red-600 text-white'
                : 'bg-[#5B8E87] text-white'
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}

type SupplementCardProps = {
  supplement: ClientSupplementWithDetails;
  onDelete: (supplementId: string, productName: string) => void;
  onEdit: (supplement: ClientSupplementWithDetails) => void;
};

function SupplementCard({ supplement, onDelete, onEdit }: SupplementCardProps) {
  const navigate = useNavigate();
  const product = supplement.product;

  if (!product) return null;

  const productDisplay = [
    product.brand && product.brand,
    product.dosage && product.dosage,
    product.form && product.form,
  ]
    .filter(Boolean)
    .join(" • ");

  // Only allow editing if payment is not completed
  const canEdit = supplement.payment_status !== 'paid';

  // Format appointment date if exists
  const appointmentDate = supplement.appointment?.starts_at
    ? new Date(supplement.appointment.starts_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  // Get payment status color
  const getLinkedPaymentBadgeColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'expired':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      {/* Appointment Context Banner - Show at top if linked to appointment */}
      {supplement.appointment && (
        <button
          onClick={() => navigate(`/appointment/${supplement.appointment!.id}`)}
          className="w-full px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 flex items-center justify-between hover:from-blue-100 hover:to-indigo-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <CalendarCheck className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              {supplement.appointment.title}
            </span>
            <span className="text-xs text-blue-600">
              {appointmentDate}
            </span>
          </div>
          <ExternalLink className="w-3.5 h-3.5 text-blue-500" />
        </button>
      )}

      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
            {productDisplay && (
              <p className="text-sm text-gray-500 mb-2">{productDisplay}</p>
            )}
          </div>
          <div className="flex gap-2">
            {canEdit && (
              <button
                onClick={() => onEdit(supplement)}
                className="text-gray-500 hover:text-[#5B8E87] p-1"
                title="Edit supplement"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => onDelete(supplement.id, product.name)}
              className="text-red-600 hover:text-red-700 p-1"
              title="Delete supplement"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <p className="text-xs text-gray-500">Quantity</p>
            <p className="font-medium text-gray-900">{supplement.quantity} units</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Amount</p>
            <p className="font-medium text-gray-900 flex items-center gap-1">
              <IndianRupee className="w-3 h-3" />
              {Number(supplement.total_amount).toFixed(2)}
            </p>
          </div>
        </div>

        {supplement.dosage_instructions && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">Dosage Instructions</p>
            <p className="text-sm text-gray-700">{supplement.dosage_instructions}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 items-center">
          {/* Payment Status with Link */}
          {supplement.payment ? (
            <button
              onClick={() => navigate(`/client/${supplement.client_id}?tab=payments`)}
              className={`px-2 py-1 rounded-full text-xs font-medium ${getLinkedPaymentBadgeColor(
                supplement.payment.status
              )} flex items-center gap-1 hover:opacity-80 transition-opacity`}
            >
              <CreditCard className="w-3 h-3" />
              {supplement.payment.status === 'paid'
                ? 'Paid'
                : supplement.payment.status === 'expired'
                ? 'Overdue'
                : `Due ${new Date(supplement.payment.due_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}`}
            </button>
          ) : (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(
                supplement.payment_status
              )}`}
            >
              {supplement.payment_status === "paid"
                ? "Paid"
                : supplement.payment_status === "partial"
                ? "Partially Paid"
                : "No Payment"}
            </span>
          )}

          {supplement.follow_up_date && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Follow-up: {new Date(supplement.follow_up_date).toLocaleDateString()}
            </span>
          )}

          {supplement.duration_days && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
              {supplement.duration_days} days
            </span>
          )}

          {supplement.batch_number && (
            <span className="text-xs text-gray-500">
              Batch: {supplement.batch_number}
            </span>
          )}
        </div>

        {supplement.notes && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Notes</p>
            <p className="text-sm text-gray-700">{supplement.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
